import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Wrench, Clock } from 'lucide-react';
// @ts-ignore - TODO: Fix this TypeScript error
import type { ServiceRequest } from '@/lib/types';

interface ServiceRequestListProps {
  onRequestClick: (request: ServiceRequest) => void;
}

// Mock data
const mockRequests: ServiceRequest[] = [
  {
    id: '1',
    title: 'Plumbing Issue in Unit 204',
    description: 'Leaking faucet needs immediate attention',
    status: 'pending',
    priority: 'high',
    createdAt: '2024-01-15T10:00:00Z',
    property: {
      id: 'prop1',
      name: 'Sunset Apartments'
    }
  },
  {
    id: '2',
    title: 'HVAC Maintenance',
    description: 'Regular maintenance check for building A',
    status: 'in_progress',
    priority: 'medium',
    createdAt: '2024-01-14T15:30:00Z',
    property: {
      id: 'prop2',
      name: 'Oak Residences'
    }
  }
];

export function ServiceRequestList({ onRequestClick }: ServiceRequestListProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');

  const filteredRequests = mockRequests.filter((request) => {
    const matchesSearch = request.title.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || request.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service Requests</CardTitle>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search requests..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              onClick={() => onRequestClick(request)}
              className="flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Wrench className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">{request.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge
                      variant={
                        request.status === 'completed' ? 'default' :
                        request.status === 'in_progress' ? 'secondary' :
                        request.status === 'cancelled' ? 'destructive' :
                        'outline'
                      }
                    >
                      {request.status.replace('_', ' ')}
                    </Badge>
                    <Badge
                      variant={
                        request.priority === 'high' ? 'destructive' :
                        request.priority === 'medium' ? 'secondary' :
                        'outline'
                      }
                    >
                      {request.priority} priority
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {request.property.name}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>{new Date(request.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}